import React, { useState, useRef, useLayoutEffect, useEffect } from "react"; // Import useEffect for language detection
import { useNavigate, useSearchParams } from "react-router-dom";
import API_BASE_URL from "./config";
import "bootstrap/dist/css/bootstrap.min.css"; // Ensure Bootstrap is imported
import "../styles.css"; // From src/components/ to src/

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultLang = searchParams.get("lang") || localStorage.getItem("language") || "en"; // Get language from URL or localStorage, default to "en"
  const [language, setLanguage] = useState(defaultLang); // State to manage language

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Create refs for the Send Code, Verify, and Sign Up buttons
  const sendCodeButtonRef = useRef(null);
  const verifyButtonRef = useRef(null);
  const signUpButtonRef = useRef(null); // Added this ref to fix the 'signUpButtonRef is not defined' error

  // Save language preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  // Force resize buttons on mount and window resize
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 576;

      if (sendCodeButtonRef.current) {
        sendCodeButtonRef.current.style.width = isMobile ? "100px !important" : "80px";
        sendCodeButtonRef.current.style.height = isMobile ? "46px !important" : "42px";
        sendCodeButtonRef.current.style.maxWidth = isMobile ? "100px !important" : "80px";
        sendCodeButtonRef.current.style.minWidth = isMobile ? "100px !important" : "80px";
        sendCodeButtonRef.current.style.maxHeight = isMobile ? "46px !important" : "42px";
        sendCodeButtonRef.current.style.minHeight = isMobile ? "46px !important" : "42px";
        sendCodeButtonRef.current.style.padding = isMobile ? "0.3rem 0.25rem !important" : "0.25rem 0.3rem";
        sendCodeButtonRef.current.style.whiteSpace = "nowrap !important";
        sendCodeButtonRef.current.style.overflow = "hidden !important";
      }

      if (verifyButtonRef.current) {
        verifyButtonRef.current.style.width = isMobile ? "100px !important" : "80px";
        verifyButtonRef.current.style.height = isMobile ? "46px !important" : "42px";
        verifyButtonRef.current.style.maxWidth = isMobile ? "100px !important" : "80px";
        verifyButtonRef.current.style.minWidth = isMobile ? "100px !important" : "80px";
        verifyButtonRef.current.style.maxHeight = isMobile ? "46px !important" : "42px";
        verifyButtonRef.current.style.minHeight = isMobile ? "46px !important" : "42px";
        verifyButtonRef.current.style.padding = isMobile ? "0.3rem 0.25rem !important" : "0.25rem 0.3rem";
        verifyButtonRef.current.style.whiteSpace = "nowrap !important";
        verifyButtonRef.current.style.overflow = "hidden !important";
        verifyButtonRef.current.className = `btn btn-primary verify-button-mobile ${isMobile ? "verify-button-mobile" : ""}`;
      }

      if (signUpButtonRef.current) {
        signUpButtonRef.current.style.width = isMobile ? "90px !important" : "120px";
        signUpButtonRef.current.style.height = isMobile ? "46px !important" : "42px";
        signUpButtonRef.current.style.maxWidth = isMobile ? "90px !important" : "120px";
        signUpButtonRef.current.style.minWidth = isMobile ? "90px !important" : "120px";
        signUpButtonRef.current.style.maxHeight = isMobile ? "46px !important" : "42px";
        signUpButtonRef.current.style.minHeight = isMobile ? "46px !important" : "42px";
        signUpButtonRef.current.style.padding = isMobile ? "0.3rem 0.5rem !important" : "0.375rem 0.75rem";
        signUpButtonRef.current.style.whiteSpace = "nowrap !important";
        signUpButtonRef.current.style.overflow = "hidden !important";
      }
    };

    // Initial call and add resize listener
    handleResize();
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty dependency array ensures this runs on mount and cleanup

  useLayoutEffect(() => {
    // Optional: Use useLayoutEffect for initial layout, but useEffect will handle dynamic resizing
    const isMobile = window.innerWidth < 576;

    if (sendCodeButtonRef.current) {
      console.log("Applying initial styles to Send Code button. Current styles:", {
        width: sendCodeButtonRef.current.style.width,
        height: sendCodeButtonRef.current.style.height,
        computedHeight: window.getComputedStyle(sendCodeButtonRef.current).height,
        computedStyles: window.getComputedStyle(sendCodeButtonRef.current),
        marginTop: sendCodeButtonRef.current.style.marginTop,
        computedMarginTop: window.getComputedStyle(sendCodeButtonRef.current).marginTop,
      });
      sendCodeButtonRef.current.style.width = isMobile ? "100px" : "80px";
      sendCodeButtonRef.current.style.height = isMobile ? "46px" : "42px"; // Increase to 46px on mobile, keep 42px on web
      sendCodeButtonRef.current.style.maxHeight = isMobile ? "46px" : "42px"; // Match height
      sendCodeButtonRef.current.style.minHeight = isMobile ? "46px" : "42px"; // Force minimum height
      sendCodeButtonRef.current.style.padding = isMobile ? "0.3rem 0.25rem" : "0.25rem 0.3rem"; // Adjust padding for 46px height on mobile
      sendCodeButtonRef.current.style.whiteSpace = "nowrap";
      sendCodeButtonRef.current.style.fontSize = "0.875rem";
      sendCodeButtonRef.current.style.backgroundColor = "#007bff";
      sendCodeButtonRef.current.style.borderColor = "#007bff";
      sendCodeButtonRef.current.style.color = "#fff";
      sendCodeButtonRef.current.style.lineHeight = "1.5";
      sendCodeButtonRef.current.style.boxSizing = "border-box";
      sendCodeButtonRef.current.style.verticalAlign = "middle";
      sendCodeButtonRef.current.style.overflow = "hidden";
      sendCodeButtonRef.current.style.marginTop = isMobile ? "0" : "0";
      console.log("Styles applied to Send Code button. New styles:", {
        width: sendCodeButtonRef.current.style.width,
        height: sendCodeButtonRef.current.style.height,
        computedHeight: window.getComputedStyle(sendCodeButtonRef.current).height,
        marginTop: sendCodeButtonRef.current.style.marginTop,
        computedMarginTop: window.getComputedStyle(sendCodeButtonRef.current).marginTop,
      });
    }

    if (verifyButtonRef.current) {
      console.log("Applying initial styles to Verify button. Current styles:", {
        width: verifyButtonRef.current.style.width,
        height: verifyButtonRef.current.style.height,
        computedHeight: window.getComputedStyle(verifyButtonRef.current).height,
      });
      verifyButtonRef.current.style.width = isMobile ? "100px !important" : "80px";
      verifyButtonRef.current.style.height = isMobile ? "46px !important" : "42px"; // Keep 46px on mobile, 42px on web for consistency
      verifyButtonRef.current.style.maxWidth = isMobile ? "100px !important" : "80px";
      verifyButtonRef.current.style.minWidth = isMobile ? "100px !important" : "80px";
      verifyButtonRef.current.style.maxHeight = isMobile ? "46px !important" : "42px";
      verifyButtonRef.current.style.minHeight = isMobile ? "46px !important" : "42px";
      verifyButtonRef.current.style.padding = isMobile ? "0.3rem 0.25rem !important" : "0.25rem 0.3rem"; // Adjust padding for 46px height on mobile
      verifyButtonRef.current.style.whiteSpace = "nowrap !important";
      verifyButtonRef.current.style.fontSize = "0.875rem !important";
      verifyButtonRef.current.style.backgroundColor = "#007bff !important";
      verifyButtonRef.current.style.borderColor = "#007bff !important";
      verifyButtonRef.current.style.color = "#fff !important";
      verifyButtonRef.current.style.lineHeight = "1.5 !important";
      verifyButtonRef.current.style.boxSizing = "border-box !important";
      verifyButtonRef.current.style.verticalAlign = "middle !important";
      verifyButtonRef.current.style.overflow = "hidden !important";
      verifyButtonRef.current.style.textOverflow = "ellipsis !important";
      verifyButtonRef.current.style.maxWidth = "100px !important"; // Extreme force for mobile
      verifyButtonRef.current.style.minWidth = "100px !important"; // Extreme force for mobile
      verifyButtonRef.current.className = `btn btn-primary verify-button-mobile ${isMobile ? "verify-button-mobile" : ""}`;
      console.log("Styles applied to Verify button. New styles:", {
        width: verifyButtonRef.current.style.width,
        height: verifyButtonRef.current.style.height,
        computedHeight: window.getComputedStyle(verifyButtonRef.current).height,
      });
    }

    if (signUpButtonRef.current) {
      console.log("Applying initial styles to Sign Up button. Current styles:", {
        width: signUpButtonRef.current.style.width,
        height: signUpButtonRef.current.style.height,
        computedHeight: window.getComputedStyle(signUpButtonRef.current).height,
      });
      signUpButtonRef.current.style.width = isMobile ? "90px" : "120px"; // Keep 90px on mobile, 120px on web
      signUpButtonRef.current.style.height = isMobile ? "46px" : "42px"; // Increase to 46px on mobile, keep 42px on web for consistency
      signUpButtonRef.current.style.maxHeight = isMobile ? "46px" : "42px";
      signUpButtonRef.current.style.minHeight = isMobile ? "46px" : "42px";
      signUpButtonRef.current.style.padding = isMobile ? "0.3rem 0.5rem" : "0.375rem 0.75rem"; // Adjust padding for 46px height on mobile
      signUpButtonRef.current.style.fontSize = "0.875rem";
      signUpButtonRef.current.style.margin = "0 auto";
      signUpButtonRef.current.style.display = "block";
      signUpButtonRef.current.style.backgroundColor = "#007bff";
      signUpButtonRef.current.style.borderColor = "#007bff";
      signUpButtonRef.current.style.color = "#fff";
      signUpButtonRef.current.style.lineHeight = "1.5";
      signUpButtonRef.current.style.boxSizing = "border-box";
      signUpButtonRef.current.style.verticalAlign = "middle";
      signUpButtonRef.current.style.overflow = "hidden";
      console.log("Styles applied to Sign Up button. New styles:", {
        width: signUpButtonRef.current.style.width,
        height: signUpButtonRef.current.style.height,
        computedHeight: window.getComputedStyle(signUpButtonRef.current).height,
      });
    }
  }, []); // Empty dependency array ensures this runs once on mount

  // Language Translations (similar to Login.js) - Updated with new message
  const text = {
    en: {
      title: "Sign Up",
      nameLabel: "Name:",
      namePlaceholder: "Enter your full name",
      emailLabel: "Email:",
      emailPlaceholder: "Enter your email",
      sendCodeButton: "Send Code",
      verificationCodeLabel: "Verification Code:",
      verificationCodePlaceholder: "Enter verification code",
      verifyButton: "Verify",
      passwordLabel: "Password:",
      passwordPlaceholder: "Create a password",
      confirmPasswordLabel: "Confirm Password:",
      confirmPasswordPlaceholder: "Confirm your password",
      signUpButton: "Sign Up",
      successMessage: "Account created successfully! Redirecting to login...",
codeVerifiedMessage: "Verification code confirmed. You can now create your password.",
verificationCodeSent: "A verification code has been sent to your email. Please check your inbox and enter the code in the verification code field to continue.",
      emailRequired: "Email is required.",
      emailInvalid: "Invalid email format.",
      verificationCodeRequired: "Verification code is required.",
      verificationCodeInvalid: "Invalid or expired verification code.",
      nameInvalid: "Name must contain only letters and spaces.",
      passwordInvalid: "Password must be at least 6 characters, with 1 uppercase letter and 1 number.",
      passwordsMismatch: "Passwords do not match.",
      failedToSendCode: "Failed to send verification code.",
      unexpectedResponse: "Unexpected response from server.",
      networkError: "Network issue. Please try again later.",
      signUpFailed: "Sign up failed.",
    },
    sw: {
      title: "Jisajili",
      nameLabel: "Jina:",
      namePlaceholder: "Ingiza jina lako kamili",
      emailLabel: "Barua Pepe:",
      emailPlaceholder: "Ingiza barua pepe yako",
      sendCodeButton: "Tuma Msimbo",
      verificationCodeLabel: "Msimbo wa Uthibitisho:",
      verificationCodePlaceholder: "Ingiza msimbo wa uthibitisho",
      verifyButton: "Thibitisha",
      passwordLabel: "Nenosiri:",
      passwordPlaceholder: "Tengeneza nenosiri",
      confirmPasswordLabel: "Thibitisha Nenosiri:",
      confirmPasswordPlaceholder: "Thibitisha nenosiri lako",
      signUpButton: "Jisajili",
      successMessage: "Akaunti imeundwa kwa mafanikio! Unaelekezwa kwenye login...",
codeVerifiedMessage: "Msimbo wa uthibitisho umethibitishwa. Sasa unaweza kuunda nenosiri lako.",
verificationCodeSent: "Msimbo wa uthibitisho umetumwa kwa barua pepe yako. Tafadhali angalia barua pepe yako na uingize msimbo kwenye sehemu ya msimbo wa uthibitisho ili kuendelea.",
      emailRequired: "Barua pepe inahitajika.",
      emailInvalid: "Umri wa barua pepe si sahihi.",
      verificationCodeRequired: "Msimbo wa uthibitisho unahitajika.",
      verificationCodeInvalid: "Msimbo wa uthibitisho si sahihi au umekwisha muda.",
      nameInvalid: "Jina lazima liwe na herufi na nafasi tu.",
      passwordInvalid: "Nenosiri lazima liwe na angalau herufi 6, na herufi kuu moja na nambari moja.",
      passwordsMismatch: "Nenosiri hazilingani.",
      failedToSendCode: "Imeshindwa kutuma msimbo wa uthibitisho.",
      unexpectedResponse: "Jibu lisilotarajiwa kutoka kwa seva.",
      networkError: "Tatizo la mtandao. Tafadhali jaribu tena baadaye.",
      signUpFailed: "Usajili umeshindwa.",
    },
  };

  // Send verification code when email is valid, including language
  const handleSendCode = async () => {
    setErrors({});
    setSuccessMessage("");

    if (!email.trim()) {
      setErrors({ email: text[language].emailRequired });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: text[language].emailInvalid });
      return;
    }

    try {
      setLoading(true);
      console.log(`Attempting to send code to ${API_BASE_URL}/auth/request-code`);
      const response = await fetch(`${API_BASE_URL}/auth/request-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, language }), // Pass the language to the API
      });

      console.log("Response status:", response.status);
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Error data:", errorData);
        setErrors({ email: errorData.error || text[language].failedToSendCode });
      } else {
        const data = await response.json();
        console.log("Success data:", data);
        if (data.message === "Verification code sent!") {
          setCodeSent(true);
          setSuccessMessage(text[language].verificationCodeSent); // Changed to new message for sending code
        } else {
          setErrors({ email: text[language].unexpectedResponse });
        }
      }
    } catch (error) {
      console.error("Error sending verification code:", error);
      setErrors({ email: text[language].networkError });
    } finally {
      setLoading(false);
    }
  };

  // Verify the entered code
  const handleVerifyCode = async () => {
    setErrors({});
    setSuccessMessage("");

    if (!verificationCode.trim()) {
      setErrors({ verificationCode: text[language].verificationCodeRequired });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, verificationCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setErrors({ verificationCode: errorData.error || text[language].verificationCodeInvalid });
      } else {
        const data = await response.json();
        if (data.valid) {
          setCodeVerified(true);
          setSuccessMessage(text[language].codeVerifiedMessage);
        } else {
          setErrors({ verificationCode: text[language].verificationCodeInvalid });
        }
      }
    } catch (error) {
      console.error("Error verifying code:", error);
      setErrors({ verificationCode: text[language].networkError });
    } finally {
      setLoading(false);
    }
  };

  // Handle signup submission
  const handleSignup = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    if (!name.trim() || !/^[A-Za-z\s]+$/.test(name)) {
      setErrors({ name: text[language].nameInvalid });
      return;
    }

    if (!/^(?=.*[A-Z])(?=.*\d).{6,}$/.test(password)) {
      setErrors({ password: text[language].passwordInvalid });
      return;
    }

    if (password !== confirmPassword) {
      setErrors({ confirmPassword: text[language].passwordsMismatch });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/complete-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setErrors({ email: errorData.error || text[language].signUpFailed });
      } else {
        setSuccessMessage(text[language].successMessage);
        // Ensure navigation works by adding a check or debug log
        console.log("Navigating to /login...");
        setTimeout(() => navigate("/login"), 2000); // Reduced timeout for faster UX
      }
    } catch (error) {
      console.error("Error completing signup:", error);
      setErrors({ email: text[language].networkError });
    } finally {
      setLoading(false);
    }
  };

  return (
    // Use a relative container to manage the fixed logo without affecting layout
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Fixed-positioned logo and text in top-left corner, separate from page elements */}
      <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 1000, padding: '10px', display: 'flex', alignItems: 'center', gap: 0 }}>
        <img
          src="/umojai-logo.png"
          alt="Umojai Logo"
          className="logo-img"
          style={{ width: '40px', height: 'auto', maxHeight: '40px', marginRight: 0 }}
        />
        <h4 className="fw-bold text-primary" style={{ margin: 0, padding: 0 }}>Umojai</h4>
      </div>

      {/* Existing signup form container, unchanged */}
      <div className="container d-flex justify-content-center align-items-center min-vh-100">
        <div className="card p-4 shadow-lg" style={{ maxWidth: "400px", width: "100%" }}>
          <h1 className="text-center mb-4">{text[language].title}</h1>
          {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}
          {Object.keys(errors).length > 0 && (
            <div className="alert alert-danger" role="alert">
              <ul className="list-unstyled mb-0">
                {Object.entries(errors).map(([key, value]) => (
                  <li key={key}>{value}</li>
                ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSignup}>
            {/* Name Field */}
            <div className="mb-3">
              <label htmlFor="name" className="form-label">{text[language].nameLabel}</label>
              <input
                type="text"
                className="form-control"
                id="name"
                placeholder={text[language].namePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={codeVerified}
                aria-label={text[language].nameLabel}
              />
            </div>

            {/* Email Field with Send Code Button */}
            <div className="mb-3">
              <label htmlFor="email" className="form-label">{text[language].emailLabel}</label>
              <div className="input-group" style={{ display: "flex !important", flexWrap: "nowrap !important", gap: "0.75rem !important", alignItems: "center !important" }}>
                <input
                  type="email"
                  className="form-control flex-grow-1"
                  id="email"
                  placeholder={text[language].emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={codeSent}
                  aria-label={text[language].emailLabel}
                  style={{ minWidth: "0 !important", flex: "1 1 auto !important", maxWidth: "unset !important" }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  ref={sendCodeButtonRef}
                  onClick={handleSendCode}
                  disabled={loading || codeSent}
                  aria-label={text[language].sendCodeButton}
                  style={{
                    whiteSpace: "nowrap !important",
                    width: window.innerWidth < 576 ? "100px !important" : "80px !important", // Keep mobile at 100px, web at 80px
                    height: window.innerWidth < 576 ? "46px !important" : "42px !important", // Increase to 46px on mobile, keep 42px on web
                    maxHeight: window.innerWidth < 576 ? "46px !important" : "42px !important", // Match height
                    minHeight: window.innerWidth < 576 ? "46px !important" : "42px !important", // Force minimum height
                    padding: window.innerWidth < 576 ? "0.3rem 0.25rem !important" : "0.25rem 0.3rem !important", // Adjust padding for 46px height on mobile
                    fontSize: "0.875rem !important",
                    backgroundColor: "#007bff !important",
                    borderColor: "#007bff !important",
                    color: "#fff !important",
                    lineHeight: "1.5 !important", // Ensure text aligns vertically
                    boxSizing: "border-box !important", // Ensure padding doesn’t increase height
                    verticalAlign: "middle !important", // Force vertical alignment
                    overflow: "hidden !important", // Prevent height overflow
                    textOverflow: "ellipsis !important", // Handle text overflow
                    marginTop: window.innerWidth < 576 ? "0 !important" : "0 !important", // Keep push down on mobile, no offset on web
                  }}
                >
                  {loading && !codeSent ? "Sending..." : text[language].sendCodeButton}
                </button>
              </div>
            </div>

            {/* Verification Code Field */}
            {codeSent && !codeVerified && (
              <div className="mb-3">
                <p style={{ fontSize: "14px", color: "#555", marginBottom: "8px" }}>
  {language === "en"
    ? "Enter the verification code sent to your email to unlock password creation."
    : "Ingiza msimbo uliotumwa kwenye barua pepe yako ili kufungua sehemu ya nenosiri."}
</p>
                <label htmlFor="verificationCode" className="form-label">{text[language].verificationCodeLabel}</label>
                <div className="input-group" style={{ display: "flex !important", flexWrap: "nowrap !important", gap: "0.75rem !important", alignItems: "center !important", flexDirection: "row !important" }}>
                  <input
                    type="text"
                    className="form-control flex-grow-1"
                    id="verificationCode"
                    placeholder={text[language].verificationCodePlaceholder}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    disabled={codeVerified}
                    aria-label={text[language].verificationCodeLabel}
                    style={{ minWidth: "0 !important", flex: "1 1 auto !important", maxWidth: "unset !important", margin: "0 !important" }}
                  />
                  <button
                    type="button"
                    id="verify-button"
                    className="btn btn-primary verify-button-mobile"
                    ref={verifyButtonRef}
                    onClick={handleVerifyCode}
                    disabled={loading || codeVerified}
                    aria-label={text[language].verifyButton}
                    style={{
                      whiteSpace: "nowrap !important",
                      width: window.innerWidth < 576 ? "100px !important" : "80px !important", // Extreme force for 100px on mobile
                      height: window.innerWidth < 576 ? "46px !important" : "42px !important", // Keep 46px on mobile, 42px on web
                      maxWidth: window.innerWidth < 576 ? "100px !important" : "80px !important", // Extreme force max-width
                      minWidth: window.innerWidth < 576 ? "100px !important" : "80px !important", // Extreme force min-width
                      maxHeight: window.innerWidth < 576 ? "46px !important" : "42px !important",
                      minHeight: window.innerWidth < 576 ? "46px !important" : "42px !important",
                      padding: window.innerWidth < 576 ? "0.3rem 0.25rem !important" : "0.25rem 0.3rem !important", // Adjust padding for 46px on mobile
                      fontSize: "0.875rem !important",
                      backgroundColor: "#007bff !important",
                      borderColor: "#007bff !important",
                      color: "#fff !important",
                      lineHeight: "1.5 !important", // Ensure text aligns vertically
                      boxSizing: "border-box !important", // Ensure padding doesn’t increase height
                      verticalAlign: "middle !important", // Force vertical alignment
                      overflow: "hidden !important", // Prevent height overflow
                      textOverflow: "ellipsis !important", // Handle text overflow
                      margin: "0 !important", // Remove any margin
                      position: "relative !important", // Ensure positioning doesn’t interfere
                      zIndex: "1000 !important", // Force z-index to prevent stacking issues
                    }}
                  >
                    {loading && !codeVerified ? "Verifying..." : text[language].verifyButton}
                  </button>
                </div>
              </div>
            )}

            {/* Password Fields (Always visible but disabled until code is verified) */}
            <div className="mb-3" style={{ opacity: codeVerified ? 1 : 0.5, pointerEvents: codeVerified ? "auto" : "none" }}>
              <label htmlFor="password" className="form-label">{text[language].passwordLabel}</label>
              <input
                type="password"
                className="form-control"
                id="password"
                placeholder={text[language].passwordPlaceholder}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!codeVerified}
                aria-label={text[language].passwordLabel}
              />
            </div>
            <div className="mb-3" style={{ opacity: codeVerified ? 1 : 0.5, pointerEvents: codeVerified ? "auto" : "none" }}>
              <label htmlFor="confirmPassword" className="form-label">{text[language].confirmPasswordLabel}</label>
              <input
                type="password"
                className="form-control"
                id="confirmPassword"
                placeholder={text[language].confirmPasswordPlaceholder}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={!codeVerified}
                aria-label={text[language].confirmPasswordLabel}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !codeVerified}
              ref={signUpButtonRef}
              aria-label={text[language].signUpButton}
              style={{
                width: window.innerWidth < 576 ? "90px !important" : "120px !important", // Keep 90px on mobile, 120px on web
                height: "42px !important", // Keep matching input height
                maxHeight: "42px !important",
                minHeight: "42px !important",
                padding: window.innerWidth < 576 ? "0.3rem 0.5rem !important" : "0.375rem 0.75rem !important", // Adjust padding for mobile
                fontSize: "0.875rem !important",
                margin: "0 auto !important",
                display: "block !important",
                backgroundColor: "#007bff !important",
                borderColor: "#007bff !important",
                color: "#fff !important",
                lineHeight: "1.5 !important", // Ensure text aligns vertically
                boxSizing: "border-box !important", // Ensure padding doesn’t increase height
                verticalAlign: "middle !important", // Force vertical alignment
                overflow: "hidden !important", // Prevent height overflow
              }}
            >
              {loading ? "Signing Up..." : text[language].signUpButton}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
